import ExcelJS from 'exceljs';
import {
  SWITCHGEAR_SHEET_MAP,
  HEADER_TO_KEY_MAP,
  REQUIRED_SWITCHGEAR_CONFIG_KEYS,
  SWITCHGEAR_WORKSHEET_NAME,
  SWITCHGEAR_WORKSHEET_COLUMNS,
  SWITCHGEAR_DEFAULT_DATA,
  WORKSHEET_PROTECTION_OPTIONS,
  EMPTY_ROWS_COUNT,
  INFO_ROW_TEXT,
} from './constants';
import { createWorkbook, downloadExcel } from '../../../../../utils';

const HEADER_KEYS = Object.keys(SWITCHGEAR_SHEET_MAP);
const CONFIG_KEYS = REQUIRED_SWITCHGEAR_CONFIG_KEYS;

const getTrimmedCellText = (cellValue) => {
  if (cellValue === null || cellValue === undefined) return '';
  if (typeof cellValue === 'string') return cellValue.trim();
  if (typeof cellValue === 'number' || typeof cellValue === 'boolean') {
    return String(cellValue).trim();
  }
  if (typeof cellValue === 'object' && 'richText' in cellValue) {
    return cellValue.richText
      .map(({ text }) => text)
      .join('')
      .trim();
  }
  if (typeof cellValue === 'object' && 'text' in cellValue) {
    return typeof cellValue.text === 'string' ? cellValue.text.trim() : '';
  }

  return '';
};

const getMissingHeaderErrors = (headers) => {
  const missingHeaderErrors = [];

  for (const expectedHeader of HEADER_KEYS) {
    if (!headers.includes(expectedHeader)) {
      missingHeaderErrors.push(`Missing required column header: "${expectedHeader}"`);
    }
  }

  return missingHeaderErrors;
};

const getRequiredConfigErrors = (parsedConfig) => {
  const requiredConfigErrors = [];

  for (const configKey of REQUIRED_SWITCHGEAR_CONFIG_KEYS) {
    if (parsedConfig[configKey].length === 0) {
      requiredConfigErrors.push(`Missing required configuration values for "${configKey}"`);
      continue;
    }

    const missingMandatoryValues = SWITCHGEAR_DEFAULT_DATA[configKey].filter(
      (requiredValue) => !parsedConfig[configKey].includes(requiredValue)
    );

    if (missingMandatoryValues.length > 0) {
      requiredConfigErrors.push(
        `Missing required values for "${configKey}": ${missingMandatoryValues.join(', ')}`
      );
    }
  }

  return requiredConfigErrors;
};

const styleHeaderRow = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { horizontal: 'center' };

  headerRow.eachCell({ includeEmpty: true }, (cell) => {
    const updatedCell = cell;
    updatedCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D9D9D9' },
    };
    updatedCell.protection = { locked: true };
  });
};

const addDefaultDataRows = (worksheet) => {
  const maxRows = Math.max(...CONFIG_KEYS.map((key) => SWITCHGEAR_DEFAULT_DATA[key].length));

  for (let rowIndex = 0; rowIndex < maxRows; rowIndex += 1) {
    const rowData = CONFIG_KEYS.map((key) => SWITCHGEAR_DEFAULT_DATA[key][rowIndex] || '');
    const dataRow = worksheet.addRow(rowData);
    dataRow.eachCell({ includeEmpty: true }, (cell) => {
      const updatedCell = cell;
      updatedCell.protection = { locked: false };
    });
  }
};

const addInfoRow = (worksheet, columnCount) => {
  const infoRow = worksheet.addRow([INFO_ROW_TEXT]);
  infoRow.font = { italic: true, color: { argb: 'FF0000FF' }, border: '1px solid red' };
  infoRow.height = 40;
  infoRow.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
  infoRow.eachCell({ includeEmpty: true }, (cell) => {
    const updatedCell = cell;
    updatedCell.protection = { locked: true };
  });

  const lastColumnLetter = String.fromCodePoint(64 + columnCount);
  worksheet.mergeCells(`A${infoRow.number}:${lastColumnLetter}${infoRow.number}`);
};

const addEmptyRows = (worksheet) => {
  const columnCount = worksheet.columns.length;
  const emptyRow = Array.from({ length: columnCount }).fill('');
  for (let index = 0; index < EMPTY_ROWS_COUNT; index += 1) {
    const dataRow = worksheet.addRow(emptyRow);
    dataRow.eachCell({ includeEmpty: true }, (cell) => {
      const updatedCell = cell;
      updatedCell.protection = { locked: false };
    });
  }
};

export const generateSwitchgearCounterConfigTemplate = () => {
  const workbook = createWorkbook();
  const worksheet = workbook.addWorksheet(SWITCHGEAR_WORKSHEET_NAME);

  worksheet.columns = SWITCHGEAR_WORKSHEET_COLUMNS;

  const columnCount = worksheet.columns.length;

  styleHeaderRow(worksheet);
  addInfoRow(worksheet, columnCount);
  addDefaultDataRows(worksheet);
  addEmptyRows(worksheet);

  worksheet.protect('', WORKSHEET_PROTECTION_OPTIONS);

  downloadExcel('xlsx', workbook, 'SwitchgearCounterConfigTemplate.xlsx');
};

export const parseSwitchgearCounterConfigTemplate = async (file) => {
  const workbook = new ExcelJS.Workbook();
  const workbookData =
    typeof Blob !== 'undefined' && file instanceof Blob && typeof file.arrayBuffer === 'function'
      ? await file.arrayBuffer()
      : file;

  await workbook.xlsx.load(workbookData);

  const worksheet = workbook.worksheets.find(({ name }) => name === SWITCHGEAR_WORKSHEET_NAME);
  const errors = [];

  if (!worksheet) {
    errors.push(`Missing required worksheet: "${SWITCHGEAR_WORKSHEET_NAME}"`);
    return { errors };
  }

  const headerRow = worksheet.getRow(1);
  const headers = [];
  headerRow.eachCell({ includeEmpty: false }, (cell) => {
    headers.push(getTrimmedCellText(cell.value));
  });

  errors.push(...getMissingHeaderErrors(headers));

  if (errors.length > 0) {
    return { errors };
  }

  const parsedConfig = {};
  for (const key of CONFIG_KEYS) {
    parsedConfig[key] = [];
  }

  const columnIndices = {};
  for (const [index, header] of headers.entries()) {
    if (HEADER_TO_KEY_MAP[header]) {
      columnIndices[HEADER_TO_KEY_MAP[header]] = index + 1;
    }
  }

  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber <= 2) return;

    for (const key of CONFIG_KEYS) {
      const cellText = getTrimmedCellText(row.getCell(columnIndices[key]).value);
      if (cellText !== '') {
        parsedConfig[key].push(cellText);
      }
    }
  });

  errors.push(...getRequiredConfigErrors(parsedConfig));

  if (errors.length > 0) {
    return { errors };
  }

  return { fileName: file.name, parsedConfig, errors };
};
